import React from 'react';
import { Runner } from '../types';
import { formatOdds } from '../utils/helpers';
import { RecentFormInline } from './RecentFormInline';
import './RunnerComparisonTable.css';

interface RunnerComparisonTableProps {
  runners: Runner[];
  aiSelection?: string;
}

// Actual DB schema: horse has recent_runs[], trainer/jockey have by_distance.distances[] with win_%
interface RecentRun {
  date?: string;
  course?: string;
  going?: string;
  pos?: string;
  dist?: string;
}
interface DistanceBucket {
  dist?: string;
  dist_f?: string;
  runners?: number;
  rides?: number;
  'win_%'?: number;
}
interface ParsedAIData {
  horse?: {
    name?: string;
    recent_form?: string;
    recent_runs?: RecentRun[];
    [key: string]: unknown;
  };
  trainer?: {
    name?: string;
    by_distance?: { distances?: DistanceBucket[] };
    distance_stats?: { win_pct?: number };
    [key: string]: unknown;
  };
  jockey?: {
    name?: string;
    by_distance?: { distances?: DistanceBucket[] };
    distance_stats?: { win_pct?: number };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const RunnerComparisonTable: React.FC<RunnerComparisonTableProps> = ({ runners, aiSelection }) => {
  // Helper function to normalize horse names (remove brackets)
  const normalizeHorseName = (name: string): string => {
    return name.replace(/\s*\([^)]*\)\s*/g, '').trim();
  };

  // Check if a runner is the AI selection
  const isAISelection = (horseName: string): boolean => {
    if (!aiSelection) return false;
    return normalizeHorseName(horseName) === normalizeHorseName(aiSelection);
  };

  // Sort runners by odds (lowest to highest)
  const sortedRunners = [...runners].sort((a, b) => {
    const oddsA = parseFloat(a.bet365_decimal_odds || '999');
    const oddsB = parseFloat(b.bet365_decimal_odds || '999');
    return oddsA - oddsB;
  });

  const parseAIData = (aiDataString: string): ParsedAIData | null => {
    try {
      return JSON.parse(aiDataString);
    } catch (error) {
      console.error('Error parsing AI data:', error);
      return null;
    }
  };

  const formatWinPercentage = (winPct: number | undefined | null): string => {
    if (winPct == null || typeof winPct !== 'number' || isNaN(winPct)) {
      return 'N/A';
    }
    return `${(winPct * 100).toFixed(0)}%`;
  };

  const formatRecentFormFromString = (form: string | undefined | null): string => {
    if (form == null || typeof form !== 'string') return 'N/A';
    const trimmed = form.trim();
    if (!trimmed) return 'N/A';
    const runs = trimmed.split(', ');
    return runs.slice(-3).join(', ');
  };

  // Build recent form from DB's recent_runs array (pos values, last 5) or legacy recent_form string
  const getRecentForm = (aiData: ParsedAIData | null): string => {
    if (!aiData?.horse) return 'N/A';
    const runs = aiData.horse.recent_runs;
    if (Array.isArray(runs) && runs.length > 0) {
      const positions = runs.map((r) => r.pos ?? '-').slice(-5);
      return positions.join(', ');
    }
    if (typeof aiData.horse.recent_form === 'string') {
      return formatRecentFormFromString(aiData.horse.recent_form);
    }
    return 'N/A';
  };

  // Get trainer win % from by_distance.distances (bucket with most runners) or legacy distance_stats
  const getTrainerWinPct = (aiData: ParsedAIData | null): number | undefined => {
    if (!aiData?.trainer) return undefined;
    const distances = aiData.trainer.by_distance?.distances;
    if (Array.isArray(distances) && distances.length > 0) {
      const best = distances.reduce((a, b) =>
        (b.runners ?? 0) > (a.runners ?? 0) ? b : a
      );
      return best['win_%'];
    }
    return aiData.trainer.distance_stats?.win_pct;
  };

  // Get jockey win % from by_distance.distances (bucket with most rides) or legacy distance_stats
  const getJockeyWinPct = (aiData: ParsedAIData | null): number | undefined => {
    if (!aiData?.jockey) return undefined;
    const distances = aiData.jockey.by_distance?.distances;
    if (Array.isArray(distances) && distances.length > 0) {
      const best = distances.reduce((a, b) =>
        (b.rides ?? b.runners ?? 0) > (a.rides ?? a.runners ?? 0) ? b : a
      );
      return best['win_%'];
    }
    return aiData.jockey.distance_stats?.win_pct;
  };

  return (
    <div className="runner-comparison-table">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Horse</th>
              <th>Odds</th>
              <th>Recent Form</th>
              <th>Trainer</th>
              <th>Trainer Win %</th>
              <th>Jockey</th>
              <th>Jockey Win %</th>
            </tr>
          </thead>
          <tbody>
            {sortedRunners.map((runner, index) => {
              const aiData = parseAIData(runner.ai_data);
              
              return (
                <tr key={`${runner.runner_id}-${index}`}>
                  <td className={`horse-name ${isAISelection(runner.horse_name) ? 'ai-selection' : ''}`}>
                    {runner.horse_name}
                  </td>
                  <td className="odds">
                    {(() => {
                      const odds = runner.bet365_decimal_odds;
                      if (!odds || isNaN(parseFloat(odds))) return 'N/A';
                      return formatOdds(parseFloat(odds));
                    })()}
                  </td>
                  <td className="recent-form">
                    <RecentFormInline text={getRecentForm(aiData)} />
                  </td>
                  <td className="trainer">
                    {aiData?.trainer?.name ?? 'N/A'}
                  </td>
                  <td className="trainer-stats">
                    {formatWinPercentage(getTrainerWinPct(aiData))}
                  </td>
                  <td className="jockey">
                    {aiData?.jockey?.name ?? 'N/A'}
                  </td>
                  <td className="jockey-stats">
                    {formatWinPercentage(getJockeyWinPct(aiData))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RunnerComparisonTable;
